import React, { useState, useEffect, lazy, Suspense } from 'react';
import './App.css';
import './i18n';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LocaleProvider } from './contexts/LocaleContext';

// Critical components - loaded immediately
import Home from './pages/Home';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import { Toaster } from './components/ui/sonner';

// Lazy loaded components for better performance
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Offres = lazy(() => import('./pages/Offres'));
const ServicePage = lazy(() => import('./pages/ServicePage'));
const AdminReviews = lazy(() => import('./pages/AdminReviews'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const Diagnostic = lazy(() => import('./pages/Diagnostic'));
const MentionsLegales = lazy(() => import('./pages/MentionsLegales'));
const Confidentialite = lazy(() => import('./pages/Confidentialite'));
const Merci = lazy(() => import('./pages/Merci'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const LegalNotice = lazy(() => import('./pages/LegalNotice'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const ChatBot = lazy(() => import('./components/ChatBot'));
const ContactFormModal = lazy(() => import('./components/ContactFormModal'));
const FooterSEO = lazy(() => import('./components/FooterSEO'));
const SEOSchema = lazy(() => import('./components/SEOSchema'));
const CookieConsent = lazy(() => import('./components/CookieConsent'));

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-white">
    <div className="text-center">
      <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B3F84]"></div>
      <p className="mt-4 text-gray-600">Loading...</p>
    </div>
  </div>
);

// Valid locales - English as default
const LOCALES = ['en', 'fr', 'es', 'de', 'it', 'pt', 'ru', 'zh', 'ar', 'hi', 'ja'];
const DEFAULT_LOCALE = 'en';

function getValidLocale(locale) {
  return LOCALES.includes(locale) ? locale : DEFAULT_LOCALE;
}

// Component to redirect to best locale based on browser/cookie
function RedirectToBestLocale() {
  const navigate = useNavigate();
  
  useEffect(() => {
    // Try to get locale from localStorage first
    let targetLocale = localStorage.getItem('i18nextLng');
    
    // If not in localStorage, detect from browser (English as fallback)
    if (!targetLocale || !LOCALES.includes(targetLocale)) {
      const browserLang = navigator.language.split('-')[0];
      targetLocale = LOCALES.includes(browserLang) ? browserLang : DEFAULT_LOCALE;
    }
    
    // Redirect to locale-prefixed URL
    navigate(`/${targetLocale}`, { replace: true });
  }, [navigate]);
  
  return <div>Redirecting...</div>;
}

// Shell component that wraps all routes and manages locale context
function Shell() {
  const { locale } = useParams();
  const { i18n } = useTranslation();
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const validLocale = getValidLocale(locale);

  useEffect(() => {
    // Sync i18n with URL locale
    if (i18n.language !== validLocale) {
      i18n.changeLanguage(validLocale);
      localStorage.setItem('i18nextLng', validLocale);
    }
    // Keep <html lang> and text direction in sync with locale (SEO + a11y + RTL)
    document.documentElement.lang = validLocale;
    document.documentElement.dir = validLocale === 'ar' ? 'rtl' : 'ltr';
  }, [validLocale, i18n]);

  return (
    <LocaleProvider>
      <ScrollToTop />
      <Suspense fallback={<PageLoader />}>
        <SEOSchema />
      </Suspense>
      <Navigation />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route index element={<Home />} />
          <Route path="services" element={<Offres />} />
          <Route path="offres" element={<Offres />} />
          <Route path="service/:slug" element={<ServicePage />} />
          <Route path="admin" element={<AdminReviews />} />
          <Route path="realisations" element={<Portfolio />} />
          <Route path="portfolio" element={<Portfolio />} />
          <Route path="assessment" element={<Diagnostic />} />
          <Route path="diagnostic" element={<Diagnostic />} />
          <Route path="blog" element={<Blog />} />
          <Route path="blog/:slug" element={<BlogPost />} />
          <Route path="legal" element={<MentionsLegales />} />
          <Route path="privacy" element={<Confidentialite />} />
          <Route path="privacy-policy" element={<PrivacyPolicy />} />
          <Route path="legal-notice" element={<LegalNotice />} />
          <Route path="terms-of-service" element={<TermsOfService />} />
          <Route path="thank-you" element={<Merci />} />
        </Routes>
      </Suspense>
      <Suspense fallback={null}>
        <FooterSEO />
      </Suspense>
      <Footer />
      <Suspense fallback={null}>
        <ChatBot onContactClick={() => setIsContactFormOpen(true)} />
        <ContactFormModal
          isOpen={isContactFormOpen}
          onClose={() => setIsContactFormOpen(false)}
        />
        <CookieConsent />
      </Suspense>
    </LocaleProvider>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/:locale/*" element={<Shell />} />
          <Route path="*" element={<RedirectToBestLocale />} />
        </Routes>
        <Toaster position="top-right" />
      </BrowserRouter>
    </div>
  );
}

export default App;
